<template>
  <div class="km-info">
    <!-- ====== TOP BAR ====== -->
    <header class="ki-top">
      <button class="ki-back" @click="back()" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="ki-title">
        <span class="ki-dot"></span>
        <h1>Profile details</h1>
      </div>
      <button class="ki-save" @click="quickSave">Save</button>
    </header>

    <!-- ====== HERO ====== -->
    <section class="ki-hero">
      <div class="ki-hero__bg"></div>
      <div class="ki-hero__body">
        <div class="ki-avatar-wrap" @click="openHerderImg()">
          <div class="ki-avatar-ring"></div>
          <img :src="userInfo.header_img || '/img/mine/avatar.png'" class="ki-avatar" alt="avatar"/>
          <span class="ki-avatar__edit">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25Zm17.71-10.04a1 1 0 0 0 0-1.41L18.37 3.4a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.77Z"/></svg>
          </span>
        </div>
        <div class="ki-hero__name">{{ userInfo.username || '—' }}</div>
        <div class="ki-hero__handle" v-if="userInfo.id">@{{ (userInfo.username || '').toLowerCase().replace(/[^a-z0-9]/g,'') || 'user' }}{{ (userInfo.id||'') }}</div>

        <div class="ki-completion">
          <div class="ki-completion__head">
            <span>Profile completion</span>
            <strong>{{ completeness }}%</strong>
          </div>
          <div class="ki-progress"><div class="ki-progress__fill" :style="{width: completeness + '%'}"></div></div>
          <div class="ki-completion__tips" v-if="incompleteFields.length">
            <span v-for="f in incompleteFields.slice(0,3)" :key="f" class="ki-tip">+ {{ f }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ====== ESSENTIALS ====== -->
    <section class="ki-section">
      <div class="ki-sec-head">Essentials</div>
      <div class="ki-card">
        <button class="ki-row" @click="openHerderImg()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#ff6b9d,#ff4d8d)">🖼️</span>
          <span class="ki-row__lbl">Profile photo</span>
          <img :src="userInfo.header_img || '/img/mine/avatar.png'" class="ki-row__avatar"/>
          <span class="ki-row__chev">›</span>
        </button>
        <button class="ki-row" @click="toSetName()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#c084fc,#a020f0)">✏️</span>
          <span class="ki-row__lbl">Display name</span>
          <span class="ki-row__val" :class="{missing: !userInfo.name}">{{ userInfo.name || 'Not set' }}</span>
          <span class="ki-row__chev">›</span>
        </button>
        <button class="ki-row" @click="toSetSex()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#60a5fa,#3b82f6)">⚧️</span>
          <span class="ki-row__lbl">Gender</span>
          <span class="ki-row__val" :class="{missing: String(userInfo.sex||'0')==='0'}">{{ sexLabel }}</span>
          <span class="ki-row__chev">›</span>
        </button>
        <button class="ki-row" @click="openDobEditor()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#fbbf24,#f59e0b)">🎂</span>
          <span class="ki-row__lbl">Date of birth</span>
          <span class="ki-row__val" :class="{missing: !localExtras.dob}">{{ localExtras.dob || 'Not set' }}</span>
          <span class="ki-row__chev">›</span>
        </button>
        <button class="ki-row" @click="openPhoneEditor()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#34d399,#10b981)">📱</span>
          <span class="ki-row__lbl">Mobile number</span>
          <span class="ki-row__val" :class="{missing: !userInfo.phone}">{{ phoneDisplay }}</span>
          <span class="ki-row__chev">›</span>
        </button>
      </div>
    </section>

    <!-- ====== ABOUT ME ====== -->
    <section class="ki-section">
      <div class="ki-sec-head">About me</div>
      <div class="ki-card">
        <div class="ki-bio">
          <textarea
            v-model="localExtras.bio"
            :placeholder="bioPlaceholder"
            maxlength="180"
            rows="3"
            @blur="saveLocalExtras"
          ></textarea>
          <div class="ki-bio__count">{{ (localExtras.bio || '').length }}/180</div>
        </div>
        <button class="ki-row" @click="openLocationEditor()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#fda4af,#f43f5e)">📍</span>
          <span class="ki-row__lbl">Location</span>
          <span class="ki-row__val" :class="{missing: !localExtras.location}">{{ localExtras.location || 'Add your city' }}</span>
          <span class="ki-row__chev">›</span>
        </button>
        <button class="ki-row" @click="openOccupationEditor()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#6366f1,#4338ca)">💼</span>
          <span class="ki-row__lbl">Occupation</span>
          <span class="ki-row__val" :class="{missing: !localExtras.occupation}">{{ localExtras.occupation || 'Add job' }}</span>
          <span class="ki-row__chev">›</span>
        </button>
        <button class="ki-row" @click="openHeightEditor()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#fdba74,#fb923c)">📏</span>
          <span class="ki-row__lbl">Height</span>
          <span class="ki-row__val" :class="{missing: !localExtras.height}">{{ localExtras.height ? localExtras.height + ' cm' : 'Add height' }}</span>
          <span class="ki-row__chev">›</span>
        </button>
      </div>
    </section>

    <!-- ====== INTERESTS ====== -->
    <section class="ki-section">
      <div class="ki-sec-head-row">
        <div class="ki-sec-head">Interests</div>
        <span class="ki-sec-hint">Choose up to 6</span>
      </div>
      <div class="ki-card ki-card--plain">
        <div class="ki-tags">
          <button
            v-for="t in INTERESTS"
            :key="t"
            class="ki-tag"
            :class="{ active: localExtras.interests.indexOf(t) !== -1, disabled: localExtras.interests.length >= 6 && localExtras.interests.indexOf(t) === -1 }"
            @click="toggleInterest(t)"
          >{{ t }}</button>
        </div>
      </div>
    </section>

    <!-- ====== LOOKING FOR ====== -->
    <section class="ki-section">
      <div class="ki-sec-head">Looking for</div>
      <div class="ki-card ki-card--plain">
        <div class="ki-chips">
          <button
            v-for="opt in LOOKING_OPTIONS"
            :key="opt.key"
            class="ki-chip-opt"
            :class="{ active: localExtras.lookingFor === opt.key }"
            @click="setLookingFor(opt.key)"
          >
            <span class="ki-chip-opt__emoji">{{ opt.emoji }}</span>
            <span>{{ opt.label }}</span>
          </button>
        </div>
      </div>
    </section>

    <!-- ====== WALLET / VERIFICATION ====== -->
    <section class="ki-section">
      <div class="ki-sec-head">Account &amp; verification</div>
      <div class="ki-card">
        <button class="ki-row" @click="toSetBank()">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#fbbf24,#f59e0b)">💳</span>
          <span class="ki-row__lbl">Crypto wallet</span>
          <span class="ki-row__val" :class="{ok: isBank, missing: !isBank}">{{ isBank ? 'Linked' : 'Not linked' }}</span>
          <span class="ki-row__chev">›</span>
        </button>
        <div class="ki-row ki-row--static">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#3b82f6,#2563eb)">✓</span>
          <span class="ki-row__lbl">Phone verified</span>
          <span class="ki-row__val" :class="{ok: !!userInfo.phone, missing: !userInfo.phone}">
            {{ userInfo.phone ? 'Verified' : 'Unverified' }}
          </span>
        </div>
        <div class="ki-row ki-row--static">
          <span class="ki-row__ico" style="background:linear-gradient(135deg,#c084fc,#a020f0)">⭐</span>
          <span class="ki-row__lbl">Credit score</span>
          <span class="ki-row__val">{{ userInfo.credit_score != null ? userInfo.credit_score : 100 }}</span>
        </div>
      </div>
    </section>

    <div class="ki-spacer"></div>

    <!-- ====== AVATAR POPUP ====== -->
    <van-popup v-model="show" position="bottom" round :style="{ height: '75%' }">
      <div class="ki-avatarbox">
        <div class="ki-avatarbox__head">
          <button class="ki-ax-close" @click="show=false" aria-label="Close">✕</button>
          <div class="ki-avatarbox__title">Choose your photo</div>
          <button class="ki-ax-save" @click="saveAvatar()">Save</button>
        </div>

        <div class="ki-ax-current">
          <img class="ki-ax-preview" :src="isActive || userInfo.header_img || '/img/mine/avatar.png'"/>
          <button class="ki-ax-upload" @click="pickFile()">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v16M4 12h16"/></svg>
            <div>
              <strong>Upload from device</strong>
              <small>JPG or PNG, up to 5MB</small>
            </div>
          </button>
          <input ref="avatarFile" type="file" accept="image/*" style="display:none" @change="onFilePicked"/>
        </div>

        <div class="ki-ax-label">Or pick a preset</div>
        <div class="ki-ax-grid">
          <img
            v-for="(url,index) in presetAvatars" :key="index"
            class="ki-ax-preset"
            :class="{ active: isActive === url }"
            :src="url"
            @click="select_header_img(url)"
          />
        </div>
      </div>
    </van-popup>

    <!-- ====== PHONE POPUP ====== -->
    <van-popup v-model="showPhoneEditor" round :close-on-click-overlay="false" class="ki-popup">
      <div class="ki-dialog">
        <div class="ki-dialog__head">
          <div class="ki-dialog__emoji">📱</div>
          <div class="ki-dialog__title">Mobile number</div>
          <div class="ki-dialog__sub">We'll never share this with anyone.</div>
        </div>
        <div class="ki-dialog__body">
          <button class="ki-phone-country" @click="showCountryPicker = true">
            <span class="ki-phone-country__flag">{{ selectedCountry.flag }}</span>
            <div class="ki-phone-country__body">
              <div class="ki-phone-country__name">{{ selectedCountry.name }}</div>
              <div class="ki-phone-country__dial">+{{ selectedCountry.dial }}</div>
            </div>
            <span class="ki-phone-country__chev">›</span>
          </button>
          <label class="ki-dialog__field">
            <span>Number</span>
            <input v-model="phoneInput" type="tel" :placeholder="$t('setting.mobile_place')"/>
          </label>
        </div>
        <div class="ki-dialog__actions">
          <button class="ki-dbtn ki-dbtn--ghost" @click="showPhoneEditor = false">Cancel</button>
          <button class="ki-dbtn ki-dbtn--primary" @click="savePhone()">Save</button>
        </div>
      </div>
    </van-popup>

    <!-- ====== COUNTRY PICKER ====== -->
    <van-popup v-model="showCountryPicker" position="bottom" round :style="{ height: '60%' }">
      <van-picker
        show-toolbar
        :title="$t('setting.country')"
        :columns="pickerColumns"
        :default-index="defaultCountryIndex"
        @cancel="showCountryPicker = false"
        @confirm="onCountryConfirm"
      />
    </van-popup>

    <!-- ====== GENERIC TEXT EDITOR ====== -->
    <van-popup v-model="textEditor.open" round :close-on-click-overlay="false" class="ki-popup">
      <div class="ki-dialog">
        <div class="ki-dialog__head">
          <div class="ki-dialog__emoji">{{ textEditor.emoji }}</div>
          <div class="ki-dialog__title">{{ textEditor.title }}</div>
          <div class="ki-dialog__sub">{{ textEditor.sub }}</div>
        </div>
        <div class="ki-dialog__body">
          <label class="ki-dialog__field">
            <span>{{ textEditor.label }}</span>
            <input
              v-model="textEditor.value"
              :type="textEditor.type || 'text'"
              :placeholder="textEditor.placeholder"
              :maxlength="textEditor.maxlength || 60"
            />
          </label>
        </div>
        <div class="ki-dialog__actions">
          <button class="ki-dbtn ki-dbtn--ghost" @click="textEditor.open = false">Cancel</button>
          <button class="ki-dbtn ki-dbtn--primary" @click="confirmTextEditor">Save</button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { countries, findCountry } from '../../assets/countries.js'

function buildPresetAvatars(){
  var seeds = ['Rose','Lily','Violet','Jasmine','Sakura','Amber','Coral','Ivy','Luna','Nova','Aria','Pearl','Ruby','Sage','Willow','Zoe','Leo','Kai','Max','Zane','Atlas','Orion','Blaze','Ace'];
  return seeds.map(function(s){
    return 'https://api.dicebear.com/7.x/adventurer/svg?seed=' + encodeURIComponent(s) + '&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf';
  });
}

const INTERESTS = [
  '☕ Coffee','📚 Books','✈️ Travel','🎬 Movies','🎵 Music','🍳 Cooking',
  '🏋️ Fitness','🎨 Art','🎮 Gaming','🧘 Yoga','🐶 Dogs','🐱 Cats',
  '🌱 Plants','📷 Photography','🍣 Sushi','🍷 Wine','🎿 Skiing','🏖️ Beach',
  '🏃 Running','🎤 Karaoke','🍻 Nightlife','🎧 Podcasts'
];

const LOOKING_OPTIONS = [
  { key:'ltr',        emoji:'💞', label:'Long-term relationship' },
  { key:'casual',     emoji:'😊', label:'Something casual' },
  { key:'friends',    emoji:'🤝', label:'New friends' },
  { key:'not_sure',   emoji:'✨', label:'Still figuring it out' }
];

export default {
  data() {
    return {
      isActive: '',
      show: false,
      isBank: false,
      userInfo: { header_img: '' },
      presetAvatars: buildPresetAvatars(),
      showPhoneEditor: false,
      showCountryPicker: false,
      phoneInput: '',
      selectedCountry: findCountry('US'),
      countries: countries,
      // KissMora client-side extras (persisted to localStorage until the backend supports them)
      localExtras: { bio: '', location: '', occupation: '', height: '', dob: '', interests: [], lookingFor: '' },
      textEditor: { open:false, field:'', title:'', sub:'', label:'', placeholder:'', value:'', emoji:'', type:'text', maxlength:60 },
      INTERESTS,
      LOOKING_OPTIONS
    };
  },
  computed: {
    phoneDisplay(){
      if(!this.userInfo || !this.userInfo.phone) return 'Not set';
      var cc = this.userInfo.country_code ? ('+' + this.userInfo.country_code + ' ') : '';
      return cc + this.userInfo.phone;
    },
    sexLabel(){
      var s = String(this.userInfo && this.userInfo.sex || '0');
      if(s === '1') return this.$t('setting.man');
      if(s === '2') return this.$t('setting.female');
      return this.$t('setting.unkown');
    },
    pickerColumns(){
      return this.countries.map(c => c.flag + '  ' + c.name + '  (+' + c.dial + ')');
    },
    defaultCountryIndex(){
      var idx = this.countries.findIndex(c => c.iso === this.selectedCountry.iso);
      return idx < 0 ? 0 : idx;
    },
    completeness(){
      let pct = 20;
      if(this.userInfo.header_img) pct += 10;
      if(this.userInfo.name) pct += 10;
      if(this.userInfo.phone) pct += 12;
      if(String(this.userInfo.sex || '0') !== '0') pct += 8;
      if(this.localExtras.dob) pct += 8;
      if(this.localExtras.bio) pct += 10;
      if(this.localExtras.location) pct += 8;
      if(this.localExtras.occupation) pct += 5;
      if(this.localExtras.height) pct += 3;
      if(this.localExtras.interests.length >= 3) pct += 4;
      if(this.localExtras.lookingFor) pct += 2;
      return Math.min(100, pct);
    },
    incompleteFields(){
      const missing = [];
      if(!this.userInfo.name) missing.push('Name');
      if(String(this.userInfo.sex || '0') === '0') missing.push('Gender');
      if(!this.localExtras.dob) missing.push('Birthday');
      if(!this.userInfo.phone) missing.push('Phone');
      if(!this.localExtras.bio) missing.push('Bio');
      if(!this.localExtras.location) missing.push('Location');
      if(this.localExtras.interests.length < 3) missing.push('Interests');
      if(!this.localExtras.lookingFor) missing.push('Looking for');
      return missing;
    },
    bioPlaceholder(){
      return "Say something about yourself — what makes you, you? ☕📚✨";
    }
  },
  methods: {
    back(){ return window.history.back(); },
    toSetName(){ this.$router.push({ path:'/Setname' }); },
    toSetBank(){ this.$router.push({ path:'/Setbank' }); },
    toSetSex(){ this.$router.push({ path:'/Setsex' }); },
    quickSave(){
      this.saveLocalExtras();
      this.$toast.success('Profile saved');
    },

    // ---- Avatar ----
    openHerderImg(){ this.isActive = this.userInfo.header_img || ''; this.show = true; },
    select_header_img(url){ this.isActive = url; },
    pickFile(){ this.$refs.avatarFile && this.$refs.avatarFile.click(); },
    onFilePicked(e){
      var file = e.target.files && e.target.files[0];
      e.target.value = '';
      if(!file) return;
      if(!/^image\//.test(file.type)){ this.$toast.fail('Please select an image'); return; }
      if(file.size > 5 * 1024 * 1024){ this.$toast.fail('Image must be under 5MB'); return; }
      var self = this;
      var fd = new FormData();
      fd.append('avatar', file);
      var loading = this.$toast.loading({ message: 'Uploading...', forbidClick: true, duration: 0 });
      this.$http({
        method: 'post', url: 'user_header_img', data: fd,
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(function(res){
        loading.clear && loading.clear();
        if(res.code === 200){
          var newUrl = (res.data && res.data.header_img) || '';
          if(newUrl){
            self.isActive = newUrl;
            self.userInfo = Object.assign({}, self.userInfo, { header_img: newUrl });
          }
          self.$toast.success('Avatar updated');
          self.show = false;
        } else {
          self.$toast.fail(res.msg || 'Upload failed');
        }
      }).catch(function(){
        loading.clear && loading.clear();
        self.$toast.fail('Upload failed');
      });
    },
    saveAvatar(){
      if(!this.isActive){ this.show = false; return; }
      if(this.isActive === this.userInfo.header_img){ this.show = false; return; }
      var self = this;
      this.$http({
        method:'post', url:'user_header_img',
        data: { header_img: this.isActive }
      }).then(function(res){
        if(res.code === 200){
          var newUrl = (res.data && res.data.header_img) || self.isActive;
          self.userInfo = Object.assign({}, self.userInfo, { header_img: newUrl });
          self.$toast.success(res.msg || 'Avatar updated');
          self.show = false;
        } else {
          self.$toast.fail(res.msg || 'Failed');
        }
      }).catch(function(){ self.$toast.fail('Failed'); });
    },

    // ---- User info ----
    getUserInfo(){
      var self = this;
      this.$http({ method:'get', url:'user_info' }).then(function(res){
        if(res.code === 200){
          self.userInfo = res.data || {};
          if(res.data && res.data.country_iso){
            var c = findCountry(res.data.country_iso);
            if(c) self.selectedCountry = c;
          }
        } else if(res.code === 401){
          self.$toast(res.msg);
        }
      });
    },

    // ---- Phone ----
    openPhoneEditor(){
      this.phoneInput = this.userInfo && this.userInfo.phone ? String(this.userInfo.phone) : '';
      this.showPhoneEditor = true;
    },
    onCountryConfirm(value, index){
      this.selectedCountry = this.countries[index] || this.selectedCountry;
      this.showCountryPicker = false;
    },
    savePhone(){
      var phone = (this.phoneInput || '').trim();
      if(!phone){ this.$toast.fail(this.$t("setting.mobile_place")); return; }
      var self = this;
      this.$http({
        method:'post', url:'user_update_phone',
        data:{ phone: phone, country_code: this.selectedCountry.dial, country_iso: this.selectedCountry.iso }
      }).then(function(res){
        if(res.code === 200){
          self.$toast.success(self.$t("setting.phone_updated"));
          self.showPhoneEditor = false;
          self.getUserInfo();
        } else {
          self.$toast.fail(res.msg);
        }
      });
    },

    // ---- Bank ----
    getUserBankInfo(){
      var self = this;
      this.$http({ method:'get', url:'user_get_bank' }).then(function(res){
        if(res.code === 200){
          self.isBank = !!(res.data && res.data.is_bank);
        } else if(res.code === 401){
          self.$toast(res.msg);
        }
      });
    },

    // ---- Local extras (bio/location/etc.) ----
    loadLocalExtras(){
      try {
        const saved = localStorage.getItem('km_profile_extras');
        if(saved){
          const parsed = JSON.parse(saved);
          Object.assign(this.localExtras, parsed);
          if(!Array.isArray(this.localExtras.interests)) this.localExtras.interests = [];
        }
      } catch(e){ /* ignore */ }
    },
    saveLocalExtras(){
      try { localStorage.setItem('km_profile_extras', JSON.stringify(this.localExtras)); } catch(e){ /* ignore */ }
    },
    toggleInterest(t){
      const i = this.localExtras.interests.indexOf(t);
      if(i !== -1){
        this.localExtras.interests.splice(i, 1);
      } else if(this.localExtras.interests.length < 6){
        this.localExtras.interests.push(t);
      } else {
        this.$toast('Max 6 interests');
        return;
      }
      this.saveLocalExtras();
    },
    setLookingFor(k){
      this.localExtras.lookingFor = this.localExtras.lookingFor === k ? '' : k;
      this.saveLocalExtras();
    },

    // ---- Generic text editor modal ----
    _openTextEditor(cfg){ this.textEditor = Object.assign({ open:true, type:'text', maxlength:60 }, cfg); },
    openDobEditor(){
      this._openTextEditor({
        field:'dob', emoji:'🎂', title:'Date of birth',
        sub:'Used to calculate your age on your profile.',
        label:'Birthday', type:'date', placeholder:'YYYY-MM-DD',
        value: this.localExtras.dob || ''
      });
    },
    openLocationEditor(){
      this._openTextEditor({
        field:'location', emoji:'📍', title:'Where are you?',
        sub:'Only your city is shown, never your exact address.',
        label:'City', placeholder:'e.g. Tokyo, New York, Paris',
        value: this.localExtras.location || '', maxlength: 60
      });
    },
    openOccupationEditor(){
      this._openTextEditor({
        field:'occupation', emoji:'💼', title:'What do you do?',
        sub:'Your job title or what you\'re studying.',
        label:'Occupation', placeholder:'e.g. Designer, Student',
        value: this.localExtras.occupation || '', maxlength: 40
      });
    },
    openHeightEditor(){
      this._openTextEditor({
        field:'height', emoji:'📏', title:'Your height',
        sub:'In centimeters.',
        label:'Height (cm)', type:'number', placeholder:'e.g. 170',
        value: this.localExtras.height || '', maxlength: 3
      });
    },
    confirmTextEditor(){
      const f = this.textEditor.field;
      if(!f){ this.textEditor.open = false; return; }
      this.localExtras[f] = (this.textEditor.value || '').trim();
      this.saveLocalExtras();
      this.textEditor.open = false;
      this.$toast.success('Updated');
    }
  },
  created() {
    if(!localStorage.getItem('token')){
      this.$router.push({ path:'/Login' });
    } else {
      this.loadLocalExtras();
      this.getUserInfo();
      this.getUserBankInfo();
    }
  }
};
</script>

<style lang="less" scoped>
.km-info {
  min-height: 100vh;
  background:
    radial-gradient(800px 400px at -10% 0%, rgba(255,77,141,.08), transparent 60%),
    radial-gradient(700px 400px at 110% 10%, rgba(160,32,240,.08), transparent 60%),
    linear-gradient(180deg, #fdf5f9 0%, #f6eef3 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  color: #2a1a3f;
  padding-bottom: 140px;
  padding-top: 56px;
}
.km-info * { box-sizing: border-box; }
button { font-family: inherit; }

/* Top bar */
.ki-top {
  position: fixed; top: 0; left: 0; right: 0; z-index: 40;
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; height: 56px;
  background: rgba(255,255,255,.85);
  backdrop-filter: blur(20px) saturate(170%);
  -webkit-backdrop-filter: blur(20px) saturate(170%);
  border-bottom: 1px solid rgba(255,77,141,.1);
}
.ki-back {
  width: 38px; height: 38px; border-radius: 12px;
  background: #fff; border: 1px solid rgba(26,16,51,.08);
  color: #2a1a3f; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .2s, transform .15s;
}
.ki-back:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.ki-title { flex: 1; display: flex; align-items: center; gap: 8px; justify-content: center; }
.ki-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 0 10px rgba(255,77,141,.6);
}
.ki-title h1 {
  margin: 0; font-size: 17px; font-weight: 800; letter-spacing: -.01em;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.ki-save {
  padding: 8px 14px; border-radius: 999px; border: none;
  background: linear-gradient(90deg, #ff4d8d, #a020f0); color: #fff;
  font-weight: 800; font-size: 13px; cursor: pointer;
  box-shadow: 0 6px 14px rgba(255,77,141,.3);
  transition: transform .15s, box-shadow .2s;
}
.ki-save:hover { transform: translateY(-1px); box-shadow: 0 10px 20px rgba(255,77,141,.45); }

/* Hero */
.ki-hero {
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, #1a1033 0%, #4a1a6e 100%);
  color: #fff;
  padding: 16px 20px 60px;
  margin-bottom: -40px;
}
.ki-hero__bg {
  position: absolute; inset: 0;
  background:
    radial-gradient(circle at 15% 20%, rgba(255,77,141,.5), transparent 55%),
    radial-gradient(circle at 90% 80%, rgba(160,32,240,.45), transparent 55%);
  pointer-events: none;
}
.ki-hero__body { position: relative; z-index: 1; text-align: center; }
.ki-avatar-wrap { position: relative; display: inline-block; cursor: pointer; margin-bottom: 12px; }
.ki-avatar-ring {
  position: absolute; inset: -5px; border-radius: 50%;
  background: conic-gradient(from 0deg, #ff4d8d, #a020f0, #ffd3e4, #ff4d8d);
  animation: kiSpin 8s linear infinite; filter: blur(6px); opacity: .85;
}
@keyframes kiSpin { to { transform: rotate(360deg); } }
.ki-avatar {
  position: relative; display: block;
  width: 88px; height: 88px; border-radius: 50%; object-fit: cover;
  border: 3px solid rgba(255,255,255,.4); background: #eee;
  box-shadow: 0 12px 28px rgba(0,0,0,.3);
}
.ki-avatar__edit {
  position: absolute; bottom: 2px; right: 2px; z-index: 2;
  width: 28px; height: 28px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #fff; box-shadow: 0 4px 10px rgba(255,77,141,.4);
}
.ki-hero__name { font-size: 20px; font-weight: 800; letter-spacing: -.01em; }
.ki-hero__handle { font-size: 13px; opacity: .75; margin-top: 2px; font-weight: 500; }

.ki-completion {
  margin: 16px auto 0; max-width: 420px;
  background: rgba(255,255,255,.13);
  border: 1px solid rgba(255,255,255,.18);
  border-radius: 14px; padding: 12px 14px;
  backdrop-filter: blur(10px);
}
.ki-completion__head {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; opacity: .9; font-weight: 600; letter-spacing: .03em;
}
.ki-completion__head strong { font-size: 16px; font-weight: 800; }
.ki-progress { height: 6px; background: rgba(255,255,255,.18); border-radius: 4px; overflow: hidden; margin: 8px 0 0; }
.ki-progress__fill {
  height: 100%; background: linear-gradient(90deg, #ffd3e4, #fff);
  border-radius: 4px; transition: width .5s ease;
}
.ki-completion__tips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.ki-tip {
  font-size: 11px; font-weight: 600;
  padding: 3px 9px; border-radius: 999px;
  background: rgba(255,255,255,.18); border: 1px solid rgba(255,255,255,.2);
  backdrop-filter: blur(8px);
}

/* Sections */
.ki-section {
  position: relative; z-index: 2;
  max-width: 640px; margin: 0 auto;
  padding: 16px 16px 0;
}
.ki-sec-head {
  font-size: 12px; font-weight: 700; color: #8273a0;
  text-transform: uppercase; letter-spacing: .08em;
  padding: 0 4px 8px;
}
.ki-sec-head-row { display: flex; align-items: baseline; justify-content: space-between; padding-right: 4px; }
.ki-sec-hint { font-size: 11px; color: #8d7fa6; padding: 0 4px 8px; }

.ki-card {
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 16px;
  border: 1px solid rgba(255,77,141,.12);
  box-shadow: 0 6px 18px rgba(255,77,141,.08), inset 0 1px 0 rgba(255,255,255,.9);
  overflow: hidden;
}
.ki-card--plain { padding: 14px; }

/* Rows */
.ki-row {
  display: flex; align-items: center; gap: 12px;
  width: 100%; padding: 13px 16px;
  background: transparent; border: none; cursor: pointer;
  text-align: left;
  border-bottom: 1px solid rgba(255,77,141,.08);
  transition: background .15s;
}
.ki-row:last-child { border-bottom: none; }
.ki-row:hover { background: rgba(255,77,141,.05); }
.ki-row--static { cursor: default; }
.ki-row--static:hover { background: transparent; }
.ki-row__ico {
  width: 34px; height: 34px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 17px; color: #fff; flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(26,16,51,.12);
}
.ki-row__lbl { flex: 1; font-size: 14px; font-weight: 600; color: #2a1a3f; }
.ki-row__val { font-size: 13px; color: #5a4a78; font-weight: 600; max-width: 50%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ki-row__val.missing { color: #b8a9c9; font-weight: 500; font-style: italic; }
.ki-row__val.ok { color: #10b981; }
.ki-row__avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,.1); }
.ki-row__chev { color: #b8a9c9; font-size: 18px; line-height: 1; }

/* Bio */
.ki-bio { padding: 14px 16px; border-bottom: 1px solid rgba(255,77,141,.08); position: relative; }
.ki-bio textarea {
  width: 100%; border: none; outline: none;
  background: transparent; color: #2a1a3f;
  font-family: inherit; font-size: 14px; font-weight: 500; line-height: 1.5;
  resize: none;
  padding-bottom: 18px;
}
.ki-bio textarea::placeholder { color: #b8a9c9; }
.ki-bio__count {
  position: absolute; bottom: 10px; right: 16px;
  font-size: 11px; color: #b8a9c9;
}

/* Tags (interests) */
.ki-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.ki-tag {
  padding: 7px 13px; border-radius: 999px; cursor: pointer;
  border: 1.5px solid rgba(255,77,141,.2);
  background: rgba(255,255,255,.7); color: #4a3d66;
  font-size: 12px; font-weight: 600; letter-spacing: .01em;
  transition: all .2s;
}
.ki-tag:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.ki-tag.active {
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  color: #fff; border-color: transparent;
  box-shadow: 0 6px 14px rgba(255,77,141,.3);
}
.ki-tag.disabled { opacity: .4; cursor: not-allowed; }

/* Chip options (looking for) */
.ki-chips { display: flex; flex-direction: column; gap: 8px; }
.ki-chip-opt {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; border-radius: 14px; cursor: pointer;
  background: rgba(255,255,255,.6); border: 1.5px solid rgba(255,77,141,.14);
  font-family: inherit; font-size: 14px; font-weight: 600; color: #2a1a3f;
  transition: all .2s;
}
.ki-chip-opt:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.ki-chip-opt.active {
  background: linear-gradient(135deg, rgba(255,77,141,.1), rgba(160,32,240,.1));
  border-color: #ff4d8d;
  box-shadow: 0 6px 14px rgba(255,77,141,.15);
}
.ki-chip-opt__emoji { font-size: 22px; }

.ki-spacer { height: 40px; }

/* ---- Avatar popup ---- */
.ki-avatarbox {
  background: #fff;
  height: 100%;
  display: flex; flex-direction: column;
  padding: 4px 0;
}
.ki-avatarbox__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255,77,141,.08);
}
.ki-ax-close, .ki-ax-save {
  border: none; cursor: pointer; font-family: inherit;
}
.ki-ax-close {
  width: 32px; height: 32px; border-radius: 50%;
  background: #f7f0f5; color: #6b5c85;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px;
}
.ki-avatarbox__title { font-size: 16px; font-weight: 800; color: #2a1a3f; }
.ki-ax-save {
  padding: 8px 18px; border-radius: 999px;
  background: linear-gradient(90deg, #ff4d8d, #a020f0); color: #fff;
  font-weight: 800; font-size: 13px;
  box-shadow: 0 6px 14px rgba(255,77,141,.3);
}
.ki-ax-current {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 16px 12px;
}
.ki-ax-preview {
  width: 92px; height: 92px; border-radius: 50%; object-fit: cover;
  border: 4px solid #ff4d8d; background: #eee;
  box-shadow: 0 6px 16px rgba(255,77,141,.3);
  flex-shrink: 0;
}
.ki-ax-upload {
  flex: 1; display: flex; align-items: center; gap: 10px;
  padding: 14px; border-radius: 14px;
  background: linear-gradient(135deg, rgba(255,77,141,.06), rgba(160,32,240,.06));
  border: 1.5px dashed rgba(255,77,141,.4);
  cursor: pointer; text-align: left;
  color: #a020f0;
}
.ki-ax-upload strong { display: block; font-size: 13px; font-weight: 800; }
.ki-ax-upload small { display: block; font-size: 11px; color: #8d7fa6; margin-top: 2px; }
.ki-ax-upload svg { flex-shrink: 0; color: #ff4d8d; }
.ki-ax-label { font-size: 12px; color: #8d7fa6; padding: 8px 18px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; }
.ki-ax-grid {
  flex: 1; overflow-y: auto;
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
  padding: 8px 16px 24px;
}
.ki-ax-preset {
  width: 100%; aspect-ratio: 1; border-radius: 50%;
  background: #faf3f8; object-fit: cover;
  border: 3px solid transparent; cursor: pointer;
  transition: transform .15s, border-color .2s;
}
.ki-ax-preset:hover { transform: scale(1.05); }
.ki-ax-preset.active { border-color: #ff4d8d; box-shadow: 0 0 0 2px rgba(255,77,141,.3); }

/* ---- Phone / text-edit popup ---- */
::v-deep .ki-popup { border-radius: 22px !important; overflow: visible; }
.ki-dialog { width: 92vw; max-width: 400px; background: #fff; border-radius: 22px; overflow: hidden; }
.ki-dialog__head {
  background: linear-gradient(135deg, #1a1033, #4a1a6e);
  padding: 20px 20px 16px; text-align: center; color: #fff;
  position: relative; overflow: hidden;
}
.ki-dialog__head::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at 50% 0%, rgba(255,77,141,.35), transparent 55%);
}
.ki-dialog__emoji { position: relative; font-size: 28px; margin-bottom: 4px; }
.ki-dialog__title { position: relative; font-size: 17px; font-weight: 800; letter-spacing: -.01em; }
.ki-dialog__sub { position: relative; font-size: 12px; opacity: .8; margin-top: 4px; }
.ki-dialog__body { padding: 16px 20px 6px; display: flex; flex-direction: column; gap: 12px; }
.ki-dialog__field { display: flex; flex-direction: column; gap: 6px; }
.ki-dialog__field span { font-size: 12px; font-weight: 700; color: #2a1a3f; letter-spacing: .01em; }
.ki-dialog__field input {
  padding: 11px 14px; font-family: inherit; font-size: 14px;
  background: #fff9fb; color: #2a1a3f; font-weight: 600;
  border: 1.5px solid rgba(26,16,51,.08); border-radius: 12px; outline: none;
  transition: border-color .2s, background .2s;
}
.ki-dialog__field input:focus { border-color: #ff4d8d; background: #fff; }
.ki-dialog__field input::placeholder { color: #b8a9c9; font-weight: 500; }

.ki-phone-country {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 12px 14px; border-radius: 12px; cursor: pointer;
  background: linear-gradient(135deg, rgba(255,77,141,.05), rgba(160,32,240,.05));
  border: 1.5px solid rgba(255,77,141,.14);
  font-family: inherit; text-align: left; color: #2a1a3f;
}
.ki-phone-country__flag { font-size: 22px; }
.ki-phone-country__body { flex: 1; min-width: 0; }
.ki-phone-country__name { font-size: 13px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ki-phone-country__dial { font-size: 11px; color: #8d7fa6; }
.ki-phone-country__chev { color: #b8a9c9; font-size: 18px; }

.ki-dialog__actions { display: flex; gap: 8px; padding: 12px 20px 18px; }
.ki-dbtn {
  flex: 1; padding: 12px; font-family: inherit;
  border-radius: 12px; border: none; cursor: pointer;
  font-size: 14px; font-weight: 700;
  transition: transform .15s, box-shadow .2s, background .2s;
}
.ki-dbtn--ghost {
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  color: #a020f0; border: 1.5px solid rgba(255,77,141,.3);
}
.ki-dbtn--ghost:hover { background: linear-gradient(135deg, rgba(255,77,141,.15), rgba(160,32,240,.15)); }
.ki-dbtn--primary {
  background: linear-gradient(90deg, #ff4d8d, #a020f0); color: #fff;
  box-shadow: 0 8px 18px rgba(255,77,141,.3);
}
.ki-dbtn--primary:hover { transform: translateY(-1px); box-shadow: 0 12px 24px rgba(255,77,141,.45); }

/* Responsive */
@media (min-width: 720px) {
  .ki-top { padding: 10px 24px; height: 64px; }
  .km-info { padding-top: 64px; }
  .ki-hero { padding: 22px 24px 72px; }
  .ki-avatar { width: 100px; height: 100px; }
  .ki-hero__name { font-size: 24px; }
  .ki-section { padding: 20px 24px 0; }
  .ki-ax-grid { grid-template-columns: repeat(6, 1fr); }
}
@media (max-width: 360px) {
  .ki-avatar { width: 76px; height: 76px; }
  .ki-row { padding: 12px 14px; gap: 10px; }
  .ki-row__lbl { font-size: 13px; }
  .ki-row__val { font-size: 12px; }
  .ki-tag { font-size: 11px; padding: 6px 11px; }
  .ki-chip-opt { font-size: 13px; padding: 11px 12px; }
}
</style>
